import React, { useState, Component } from 'react';
import { Button } from '../ui/button';
import { Copy, Share2, Twitter, Facebook, MessageCircle, Link as LinkIcon, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getPublicTranscriptPageUrl } from '../../lib/api';
interface ShareTranscriptProps {
  taskId: string;
  title?: string;
  className?: string;
  variant?: 'default' | 'compact';
}
export function ShareTranscript({
  taskId,
  title = 'TikTok Transcript',
  className,
  variant = 'default'
}: ShareTranscriptProps) {
  const [copied, setCopied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const shareUrl = getPublicTranscriptPageUrl(taskId, 'app');
  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(shareUrl);
  const shareText = `Check out this transcript from ${title} via ScribeTok`;
  const encodedShareText = encodeURIComponent(shareText);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const toggleShareOptions = () => {
    setShowShareOptions(!showShareOptions);
  };
  // Share to Twitter
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedUrl}`, '_blank');
  };
  // Share to Facebook
  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
  };
  // Share via SMS
  const shareViaSms = () => {
    window.open(`sms:?body=${encodedShareText} ${encodedUrl}`, '_blank');
  };
  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedShareText} ${encodedUrl}`, '_blank');
  };
  if (variant === 'compact') {
    return <div className={cn('relative', className)} data-id="element-1540">
        <Button variant="outline" size="sm" className="gap-2" onClick={toggleShareOptions} data-id="element-1541">
          <Share2 size={16} data-id="element-1542" />
          Share
        </Button>
        {showShareOptions && <div className="absolute top-full right-0 mt-2 p-3 bg-card rounded-lg border border-border shadow-lg z-10 w-64" data-id="element-1543">
            <div className="grid grid-cols-2 gap-2" data-id="element-1544">
              <Button variant="ghost" size="sm" className="justify-start" onClick={handleCopyLink} data-id="element-1545">
                {copied ? <Check size={14} className="mr-2" data-id="element-1546" /> : <Copy size={14} className="mr-2" data-id="element-1547" />}
                Copy Link
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareToTwitter} data-id="element-1548">
                <Twitter size={14} className="mr-2" data-id="element-1549" />
                Twitter
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareToFacebook} data-id="element-1550">
                <Facebook size={14} className="mr-2" data-id="element-1551" />
                Facebook
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareViaSms} data-id="element-1552">
                <MessageCircle size={14} className="mr-2" data-id="element-1553" />
                SMS
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start mt-2" onClick={shareViaWhatsApp} data-id="element-1554">
              <span className="mr-2" data-id="element-1555">📱</span>
              WhatsApp
            </Button>
          </div>}
      </div>;
  }
  return <div className={cn('space-y-4', className)} data-id="element-1556">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1557">
        <h3 className="font-semibold text-base flex items-center gap-2" data-id="element-1558">
          <Share2 size={18} className="text-primary" data-id="element-1559" />
          Share This Transcript
        </h3>
        <div className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full whitespace-nowrap" data-id="element-1560">
          Share with friends
        </div>
      </div>

      {/* Copy Link Input */}
      <div className="flex gap-2" data-id="element-1561">
        <input type="text" value={shareUrl} readOnly className="flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm truncate" onClick={e => e.currentTarget.select()} data-id="element-1562" />
        <Button variant="default" size="sm" onClick={handleCopyLink} className="flex-shrink-0 h-auto px-4" data-id="element-1563">
          {copied ? <>
              <Check size={16} data-id="element-1564" />
              <span className="ml-2 hidden sm:inline" data-id="element-1565">Copied!</span>
            </> : <>
              <Copy size={16} data-id="element-1566" />
              <span className="ml-2 hidden sm:inline" data-id="element-1567">Copy</span>
            </>}
        </Button>
      </div>

      {/* Share Buttons - Responsive Grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3" data-id="element-1568">
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareToTwitter} data-id="element-1569">
          <Twitter size={16} className="sm:mr-2" data-id="element-1570" />
          <span className="hidden sm:inline" data-id="element-1571">Twitter</span>
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareToFacebook} data-id="element-1572">
          <Facebook size={16} className="sm:mr-2" data-id="element-1573" />
          <span className="hidden sm:inline" data-id="element-1574">Facebook</span>
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareViaSms} data-id="element-1575">
          <MessageCircle size={16} className="sm:mr-2" data-id="element-1576" />
          <span className="hidden sm:inline" data-id="element-1577">SMS</span>
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-center h-11" onClick={shareViaWhatsApp} data-id="element-1578">
          <span className="text-base sm:mr-2" data-id="element-1579">📱</span>
          <span className="hidden sm:inline" data-id="element-1580">WhatsApp</span>
        </Button>
      </div>

      {/* Helper Text */}
      <div className="text-xs text-muted-foreground flex items-center justify-center gap-1.5 pt-1" data-id="element-1581">
        <LinkIcon size={12} data-id="element-1582" />
        <span data-id="element-1583">Help friends save time with this transcript</span>
      </div>
    </div>;
}